import { useSelector } from 'react-redux';
import { get } from 'lodash';
import ModeComment from '@lendi-ui/icon/ModeComment';

import {
  FIELD_PATHS,
  FREQUENCY,
  GOVT_BENEFIT_TYPE_OPTIONS,
  INCOME_TYPE,
  PII_FIELD_MASKING_CLASS,
} from '@funnels/constants';
import {
  AnyFormValues,
  Asset,
  Company,
  CompanyOwner,
  Employment,
  FormValuesIncomesExpenses,
  Income,
  Owner,
  ReduxStoreData,
  StoreEmployment,
} from '@funnels/types';
import {
  filterOutPreviousIncomes,
  getAnnualValueFromFrequency,
  getApplicantsFullnameFromOwners,
  getFieldValueLabel,
  getSelfEmployedYearlyIncomeAmount,
} from '@funnels/utils';

import { StringDisplay } from '../../../Fields';
import CommentsButton from '../../../Layouts/_common/CommentsButton';
import {
  IncomeAmountFunnelReadOnlyView,
  IncomeFrequencyFunnelReadOnlyView,
  IncomeOwnershipReadOnlyView,
  IncomeTypeFunnelReadOnlyView,
} from '../../../Questions';
import Table from '../../../Table';

const HEADERS = [
  { text: 'Category', min: 220, max: 320 },
  { text: 'Related', min: 220, max: 320 },
  { text: 'Owner', min: 240, max: 340 },
  { text: 'Amount', min: 220, max: 320 },
  { text: 'Frequency', min: 220, max: 320 },
  { text: 'Annual total', min: 220, max: 320 },
  { text: '', min: 60, max: 120 },
];

function getRelatedField(income: Income, { employments = [], assets = [], companies = [] }: FormValuesIncomesExpenses) {
  const employmentId = get({ incomes: [income] }, FIELD_PATHS.INCOME_EMPLOYMENT_ID(0));
  const assetId = get({ incomes: [income] }, FIELD_PATHS.INCOME_ASSET_ID(0));
  const incomeType = get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0));
  const benefitType = get({ incomes: [income] }, FIELD_PATHS.INCOME_BENEFIT_TYPE(0));
  const description = get({ incomes: [income] }, FIELD_PATHS.INCOME_DESCRIPTION(0));
  const companyClientId = get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0));

  if (employmentId) {
    const employment = employments.find((employment: StoreEmployment) => employment.clientId === employmentId);

    if (employment) {
      return get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_BUSINESS_NAME(0));
    }
  } else if (assetId) {
    const property = assets.find((property: Asset) => property.clientId === assetId);

    if (property) {
      return get({ assets: [property] }, FIELD_PATHS.ASSET_FORMATTED_ADDRESS(0));
    }
  } else if (companyClientId) {
    const company = companies.find(
      (company: Company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
    );
    if (company) {
      return get({ companies: [company] }, FIELD_PATHS.COMPANY_BUSINESS_NAME(0));
    }
  }

  if (incomeType === INCOME_TYPE.GOVT_BENEFIT) {
    const option = GOVT_BENEFIT_TYPE_OPTIONS.find((gbOption) => gbOption.value === benefitType);
    return option?.label;
  }

  if (incomeType === INCOME_TYPE.OTHER_INCOME) {
    return description;
  }
}

function mapState(currentState: ReduxStoreData) {
  const data: FormValuesIncomesExpenses = {
    applicants: currentState.applicants.data,
    incomes: currentState.incomes.data,
    expenses: currentState.expenses.data,
    assets: currentState.assets.data,
    employments: currentState.employments.data,
    companies: currentState.companies.data,
  };

  const error =
    currentState.applicants.error ||
    currentState.incomes.error ||
    currentState.expenses.error ||
    currentState.companies.error;
  const loading =
    currentState.applicants.loading +
    currentState.incomes.loading +
    currentState.expenses.loading +
    currentState.companies.loading;

  return { data, error, loading };
}

export interface IncomesTableProps {
  handleShowModal: () => void;
}

function IncomesTable(props: IncomesTableProps) {
  const { data } = useSelector(mapState);
  const applicants = get(data, FIELD_PATHS.APPLICANTS(), []);
  const incomes = get(data, FIELD_PATHS.INCOMES(), []);
  const employments = get(data, FIELD_PATHS.EMPLOYMENTS(), []);
  const filteredIncomes = filterOutPreviousIncomes(incomes, employments);
  const companies = get(data, FIELD_PATHS.COMPANIES(), []);

  return (
    <Table
      title="Income"
      headers={HEADERS}
      after={
        filteredIncomes.length ? (
          <CommentsButton handleShowModal={props.handleShowModal}>View comments</CommentsButton>
        ) : null
      }
    >
      {filteredIncomes.length > 0 && (
        <tbody data-component="table-tbody">
          {filteredIncomes.map((income: Income, index: number) => {
            const incomeType = get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0));
            const isSEV2Income = [
              INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC,
              INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC,
            ].includes(incomeType);
            const incomeTypeLabel = getFieldValueLabel(incomeType, incomeType);

            const related = getRelatedField(income, data);
            const owners = get({ incomes: [income] }, FIELD_PATHS.INCOME_OWNERS(0));
            const ownersFullName = getApplicantsFullnameFromOwners(owners, applicants);
            const incomeAmount = get({ incomes: [income] }, FIELD_PATHS.INCOME_AMOUNT(0));
            const incomeFrequency = get({ incomes: [income] }, FIELD_PATHS.INCOME_FREQUENCY(0));
            const annualTotal =
              incomeAmount && incomeFrequency ? getAnnualValueFromFrequency(incomeAmount, incomeFrequency) : undefined;
            const hasComment = get({ incomes: [income] }, FIELD_PATHS.INCOME_COMMENTS(0));

            // SE v2 incomes
            if (isSEV2Income) {
              const companyClientId = get({ incomes: [income] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0));
              const company = companies.find(
                (company: Company) =>
                  get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
              );
              const companyOwners = get({ companies: [company] }, FIELD_PATHS.COMPANY_OWNERS(0), []);

              // split SE v2 incomes by ownership
              return companyOwners.map((companyOwner: CompanyOwner, ownerIndex: number) => {
                const ownerLabelText =
                  getApplicantsFullnameFromOwners([companyOwner] as Owner[], applicants) || undefined;

                const employmentIndex = employments.findIndex(
                  (employment: Employment) =>
                    get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(0)) ===
                      companyClientId &&
                    get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) ===
                      companyOwner.applicantId
                );

                // don't display orphan income
                if (employmentIndex === -1) {
                  return null;
                }

                const yearlyIncomeAmount = getSelfEmployedYearlyIncomeAmount(employmentIndex, data as AnyFormValues);

                return (
                  <tr
                    key={`${income.clientId}-${ownerIndex}` || `new-income-${ownerIndex}`}
                    data-component="table-row-income"
                  >
                    <td>
                      <IncomeTypeFunnelReadOnlyView value={incomeTypeLabel} textSize="md" />
                    </td>
                    <td>
                      <StringDisplay value={related} textSize="md" />
                    </td>
                    <td>
                      <StringDisplay value={ownerLabelText[0]} textSize="md" className={PII_FIELD_MASKING_CLASS} />
                    </td>
                    <td>
                      <IncomeAmountFunnelReadOnlyView value={yearlyIncomeAmount} textSize="md" />
                    </td>
                    <td>
                      <IncomeFrequencyFunnelReadOnlyView value={FREQUENCY.YEARLY} textSize="md" />
                    </td>
                    <td>
                      <IncomeAmountFunnelReadOnlyView value={yearlyIncomeAmount} textSize="md" />
                    </td>
                    <td></td>
                  </tr>
                );
              });
            }

            return (
              <tr key={income.clientId}>
                <td>
                  <IncomeTypeFunnelReadOnlyView value={incomeTypeLabel} textSize="md" />
                </td>
                <td>{related && <StringDisplay value={related} textSize="md" />}</td>
                <td>
                  <IncomeOwnershipReadOnlyView value={ownersFullName} textSize="md" />
                </td>
                <td>
                  <IncomeAmountFunnelReadOnlyView value={incomeAmount} textSize="md" />
                </td>
                <td>
                  <IncomeFrequencyFunnelReadOnlyView value={incomeFrequency} textSize="md" />
                </td>
                <td>
                  <IncomeAmountFunnelReadOnlyView value={annualTotal} textSize="md" />
                </td>
                <td>{hasComment && <ModeComment />}</td>
              </tr>
            );
          })}
        </tbody>
      )}
    </Table>
  );
}

export default IncomesTable;
